<template>
  <div class="dataWrapper w-full h-full flex justify-start items-start flex-col">
    <div 
      v-if="bcOverviewsModalStore.type === 'document'"
      class="visitsWrapper w-full flex justify-start items-start flex-col"
    >
      <overviews-tab-header 
        v-model="searchQueryVisits"
        :show-search="type === 'visits'"
      >
        <button 
          class="flex justify-start items-center"
          @click.stop.prevent="handleToggle('visits')"
        >
          <h2 class="block text-lg font-medium">
            {{ $t('Last visit') }}
          </h2>
          <aw-icon 
            :icon="type === 'visits' ? 'chevron-up' : 'chevron-down'" 
            class="toggleIcon w-8 h-8 ml-2 text-gray-400"
          />
        </button>
      </overviews-tab-header>
      <overviews-loading v-if="type === 'visits' && loading"/>
      <overviews-loading-error v-else-if="type === 'visits' && loadingError"/>
      <overviews-no-results v-else-if="type === 'visits' && filteredLastVisitsBySearch?.length === 0"/>
      <div 
        v-else-if="type === 'visits' && filteredLastVisitsBySearch?.length > 0"
        class="w-full flex justify-start items-start flex-col"
      >
        <overviews-stats-visit
          v-for="(visit, i) in filteredLastVisitsBySearch"
          :key="i"
          :visit="visit"
        />
      </div>
    </div>
    <div 
      class="downloadsWrapper w-full flex justify-start items-start flex-col mb-14"
      :class="{'mt-6': bcOverviewsModalStore.type === 'document'}"
    >
      <overviews-tab-header 
        v-model="searchQueryDownloads"
        :show-search="type === 'downloads'"
      >
        <button 
          class="headerWrapper flex justify-start items-center"
          @click.stop.prevent="handleToggle('downloads')"
        >
          <h2 class="block text-lg font-medium">
            {{ $t('Downloads') }}
          </h2>
          <aw-icon 
            :icon="type === 'downloads' ? 'chevron-up' : 'chevron-down'" 
            class="toggleIcon w-8 h-8 ml-2 text-gray-400"
          />
        </button>
      </overviews-tab-header>
      <overviews-loading v-if="type === 'downloads' && loading"/>
      <overviews-loading-error v-else-if="type === 'downloads' && loadingError"/>
      <overviews-no-results v-else-if="type === 'downloads' && filteredDownloadsBySearch?.length === 0"/>
      <div 
        v-else-if="data && type === 'downloads' && filteredDownloadsBySearch?.length > 0"
        class="downloadItemsWrapper w-full flex justify-start items-start flex-col"
      >
        <overviews-stats-download
          v-for="(file, i) in filteredDownloadsBySearch"
          :key="i"
          :file="file"
        />
      </div>
    </div>
    <span 
      v-if="data"
      class="mt-auto text-base text-gray-400"
    >
      {{ $t('Data size') }}: {{ formatBytes(data.size) }}
    </span>
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import { search } from 'ss-search'
import formatDate from 'nette-base-module/js/utils/formatDate'
import useBcOverviewsModalStore from '@/frontapp/store/bcOverviewsModal'
import { DocumentApi, FolderApi } from '@/frontapp/test-api'

const documentApi = new DocumentApi({}, '/api/v2')
const folderApi = new FolderApi({}, '/api/v2')

export default {
  props: {
  },
  data() {
    return {
      searchQueryVisits: null,
      searchQueryDownloads: null,
      type: 'visits',
      loading: false,
      loadingError: false,
      data: null,
      apiHandler: null,
    }
  },
  computed: {
    ...mapStores(useBcOverviewsModalStore),
    filteredLastVisitsBySearch() {
      if (!this.data || this.bcOverviewsModalStore.type === 'folder') {
        return null
      }

      let searchedVisits = this.data?.last_visits
      const days = new Map()

      if (this.searchQueryVisits) {
        searchedVisits = search(this.data?.last_visits, ['user.name', 'user.surname', 'user.email'], this.searchQueryVisits)
      }
      
      searchedVisits.forEach(visit => {
        const dateId = formatDate(visit.viewed, 'd. m. Y')

        if (!days.has(dateId)) {
          days.set(dateId, [])
        }

        days.get(dateId).push(visit)
      })

      return Array.from(days, ([viewed, visitors]) => ({ viewed, visitors }))
    },
    filteredDownloadsBySearch() {
      if (!this.searchQueryDownloads || this.searchQueryDownloads.length < 1) {
        return this.data?.downloads
      }

      const searchedDownloads = search(this.data.downloads, ['filename'], this.searchQueryDownloads)
      return searchedDownloads
    },
  },
  methods: {
    async handleApi() {
      this.loading = true
      this.loadingError = false

      if (this.bcOverviewsModalStore.type === 'document') {
        this.apiHandler = documentApi
      } else {
        this.apiHandler = folderApi
      }
    
      try {
        const response = await this.apiHandler[this.bcOverviewsModalStore.type + 'Stats'](this.bcOverviewsModalStore.id)
        this.data = response.data
      } catch (err) {
        this.loadingError = true
      } finally {
        this.loading = false
      }
    },
    handleToggle(type) {
      if (this.type === type) {
        this.type = null
      } else {
        this.type = type
      }
    }
  },
  mounted() {
    if (this.bcOverviewsModalStore.type === 'folder') {
      this.type = 'downloads'
    }

    this.handleApi()
  }
}
</script>

<style lang="scss" scoped>
.visitsWrapper .headerWrapper:hover,
.downloadsWrapper .headerWrapper:hover {
  .toggleIcon {
    @apply text-bc-blue;
  }
}

.dataWrapper:has(.visitsWrapper) .downloadsWrapper .headerWrapper {
  margin-top: 10px !important;
}
</style>
